import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getFeatures } from '../api';
import { useAuth } from './AuthContext';
import { normalizeFeatures } from '../utils/featureFlags';

const FeaturesContext = createContext(null);

// Tenant feature flags from the server feature registry (plan 041):
// GET /tenant-settings/features → { features, employee_home, catalog }.
// Before the first load, on a first-load error, or when logged out every
// feature sits at its registry default. A failed REFRESH keeps the last good
// state — a blip must not flip a tenant's real flags back to defaults.
// Consumers that act on a flag (tab list, gated fetches) wait for `loaded`.
// `loaded` is true only once a fetch has settled for the CURRENT user (derived
// each render), so it is false from the very first render after a login or
// user switch.
const DEFAULT_STATE = normalizeFeatures(null);

export function FeaturesProvider({ children }) {
  const { user } = useAuth();
  // Key on identity, not the user object: profile edits and the employeeType
  // hydration in App.js replace the object without changing who is logged in.
  const userId = user?.id ?? null;
  const [state, setState] = useState(DEFAULT_STATE);
  // Identity the last fetch SETTLED for (success, or a first-load error that
  // fell back to defaults): undefined = nothing yet, null = logged out, else
  // a user id. `loaded` is derived from it during render, never reset by an
  // effect: child effects run before this provider's effects, so a boolean
  // reset in an effect left one commit after login with loaded=true and the
  // logged-out defaults (time_off on). App.js and AdminHome fired their gated
  // PTO count in that commit (Gitea #94).
  const [settledFor, setSettledFor] = useState(undefined);
  const loaded = settledFor === userId;
  // userId the current state was successfully loaded for (null = never)
  const loadedFor = useRef(null);
  const currentUserId = useRef(userId);
  currentUserId.current = userId;

  const refresh = useCallback(async () => {
    if (!userId) {
      loadedFor.current = null;
      setState(DEFAULT_STATE);
      setSettledFor(null);
      return;
    }
    try {
      const res = await getFeatures();
      if (currentUserId.current !== userId) return; // logged out / switched user mid-flight
      loadedFor.current = userId;
      setState(normalizeFeatures(res.data));
    } catch (err) {
      if (currentUserId.current !== userId) return;
      // Keep the previous good state on a refresh error; only a user who has
      // never loaded falls back to the defaults.
      if (loadedFor.current !== userId) setState(DEFAULT_STATE);
    } finally {
      if (currentUserId.current === userId) setSettledFor(userId);
    }
  }, [userId]);

  // A different user must not see the previous user's flags while loading.
  useEffect(() => {
    if (loadedFor.current !== userId) setState(DEFAULT_STATE);
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Tasks.js dispatches this after PUT /tasks/settings.
  useEffect(() => {
    window.addEventListener('hourtrax:tasks-settings-changed', refresh);
    return () => window.removeEventListener('hourtrax:tasks-settings-changed', refresh);
  }, [refresh]);

  const value = useMemo(() => ({
    features: state.features,
    employeeHome: state.employeeHome,
    catalog: state.catalog,
    accrualWarning: state.accrualWarning,
    loaded,
    refresh,
    isEnabled: (key) => !!state.features[key],
  }), [state, loaded, refresh]);

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures() {
  const ctx = useContext(FeaturesContext);
  if (!ctx) throw new Error('useFeatures must be used within a FeaturesProvider');
  return ctx;
}
